if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtons = Array.from(this.querySelectorAll('button[data-product-form-role]'));
        this.submitButton =
          this.submitButtons.find((button) => button.dataset.productFormRole === 'primary') ||
          this.querySelector('[type="submit"]');

        if (this.submitButton && !this.submitButtons.includes(this.submitButton)) {
          this.submitButtons.unshift(this.submitButton);
        }

        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.activeSubmitButton = null;
        this.activeSubmitButtonText = null;

        if (document.querySelector('cart-drawer')) {
          this.submitButtons.forEach((button) => button.setAttribute('aria-haspopup', 'dialog'));
        } else if (this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.quantityBreaks = document.getElementById(`quantity-breaks-${this.dataset.sectionId}`);
        this.customFields = document.querySelectorAll(`[id^='CustomField-${this.dataset.sectionId}-']`);
        this.variantInputs = this.form.querySelector(".product-form__variants");
      }

      /**
       * Get quantity radio buttons - they may be outside the form but linked via form attribute
       * @returns {Array}
       */
      getQuantityRadios() {
        const formId = this.form.id;
        let radios = [];
        
        // First try inside the form
        const inFormRadios = this.form.querySelectorAll('input[type="radio"][name="quantity"]');
        if (inFormRadios.length > 0) {
          radios = Array.from(inFormRadios);
        } else if (formId) {
          // Try finding radios linked via form attribute (quantity-breaks are outside the form element)
          const linkedRadios = document.querySelectorAll(`input[type="radio"][name="quantity"][form="${formId}"]`);
          radios = Array.from(linkedRadios);
        }
        
        // If still no radios found, try searching in the same product-info section
        if (radios.length === 0) {
          const productInfo = this.closest('.product__info-container, .product__info-wrapper, [id^="ProductInfo-"]');
          if (productInfo) {
            const sectionRadios = productInfo.querySelectorAll('input[type="radio"][name="quantity"]');
            radios = Array.from(sectionRadios);
          }
        }
        
        return radios;
      }

      /**
       * Get current quantity from form - handles both regular input and radio buttons (quantity breaks)
       * @returns {number}
       */
      getCurrentQuantity() {
        // First check for quantity breaks (radio buttons) - may be outside form
        const quantityRadios = this.getQuantityRadios();
        if (quantityRadios.length > 0) {
          const checkedRadio = quantityRadios.find(r => r.checked);
          if (checkedRadio) {
            const qty = parseInt(checkedRadio.value, 10) || 1;
            return qty;
          }
        }
        
        // Fall back to regular quantity input inside form
        const quantityInput = this.form.querySelector('input[name="quantity"]:not([type="radio"])');
        if (quantityInput) {
          return parseInt(quantityInput.value, 10) || 1;
        }
        
        return 1;
      }

      /**
       * Update quantity in form - handles both regular input and radio buttons (quantity breaks)
       * @param {number} newQuantity
       */
      updateQuantity(newQuantity) {
        // First check for quantity breaks (radio buttons)
        const quantityRadios = this.getQuantityRadios();
        if (quantityRadios.length > 0) {
          // Find a radio with matching or closest higher quantity
          let targetRadio = null;
          let closestMatch = null;
          let closestDiff = Infinity;
          
          quantityRadios.forEach(radio => {
            const radioQty = parseInt(radio.value, 10);
            if (radioQty === newQuantity) {
              targetRadio = radio;
            } else if (radioQty >= newQuantity && (radioQty - newQuantity) < closestDiff) {
              closestDiff = radioQty - newQuantity;
              closestMatch = radio;
            }
          });
          
          // Use exact match or closest higher quantity
          const radioToSelect = targetRadio || closestMatch;
          if (radioToSelect) {
            radioToSelect.checked = true;
            radioToSelect.dispatchEvent(new Event('change', { bubbles: true }));
            // Also click the associated label for visual update
            const label = document.querySelector(`label[for="${radioToSelect.id}"]`);
            if (label) {
              label.click();
            }
          }
          return;
        }
        
        // Fall back to regular quantity input
        const quantityInput = this.form.querySelector('input[name="quantity"]:not([type="radio"])');
        if (quantityInput) {
          quantityInput.value = newQuantity;
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        const submitter = evt.submitter || this.submitButton;
        if (!submitter) return;

        if (submitter.getAttribute('aria-disabled') === 'true') return;

        // Check if this is a skip-cart submission (Commander maintenant)
        const isSkipCart = submitter?.dataset.skipCart === 'true';
        
        // Get current quantity from form (handles radio buttons for quantity breaks)
        const currentQuantity = this.getCurrentQuantity();

        // If skip cart is enabled, check free shipping modal first
        if (isSkipCart && typeof window.checkFreeShippingBeforeOrder === 'function') {
          window.checkFreeShippingBeforeOrder(currentQuantity, (finalQuantity) => {
            // Update quantity if adjusted
            if (finalQuantity !== currentQuantity) {
              this.updateQuantity(finalQuantity);
            }
            // Proceed with submission
            this.processSubmission(submitter);
          });
        } else {
          // No skip cart or no modal - proceed directly
          this.processSubmission(submitter);
        }
      }

      processSubmission(submitter) {
        this.activeSubmitButton = submitter;
        this.activeSubmitButtonText = this.activeSubmitButton.querySelector('span') || this.submitButtonText;

        this.handleErrorMessage();
        this.error = false;

        this.activeSubmitButton.setAttribute('aria-disabled', true);
        const computedStyles = window.getComputedStyle(this.activeSubmitButton);
        const spinnerColor = computedStyles?.color;
        this.activeSubmitButton.classList.add('loading');
        const activeSpinner =
          this.activeSubmitButton.querySelector('.loading__spinner') || this.querySelector('.loading__spinner');
        if (spinnerColor && spinnerColor !== 'transparent' && spinnerColor !== 'rgba(0, 0, 0, 0)') {
          this.activeSubmitButton.style.setProperty('--spinner-color', spinnerColor);
          this.activeSubmitButton.style.setProperty('--loading-text-color', spinnerColor);
          if (activeSpinner) {
            activeSpinner.style.setProperty('--spinner-color', spinnerColor);
          }
        }
        if (activeSpinner) {
          activeSpinner.classList.remove('hidden');
        }
        
        let hasVariants = false;
        if (this.quantityBreaks && this.variantInputs && this.quantityBreaks.formVariants?.length > 0) {
          let quantityInputs = "";
          hasVariants = true;
          for (let i = 0; i < this.quantityBreaks.formVariants.length; i++) {
              let variant = this.quantityBreaks.formVariants[i];
              quantityInputs += `<input type="hidden" name="items[${i}][quantity]" value="${variant.quantity}">`;
              quantityInputs += `<input type="hidden" name="items[${i}][id]" value="${variant.id}">`;
              this.customFields.forEach(field => {
                  quantityInputs += `<input type="hidden" name="items[${i}][properties][${field.fieldName}]" value="${field.value}">`;
              });
          }
          this.variantInputs.innerHTML = quantityInputs;
        }
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        if (hasVariants) {
          let options = this.dataset.options ? this.dataset.options.split(",") : [];
          let fieldsToDelete = ["id", "quantity", ...options];
          for (let field of fieldsToDelete) {
              formData.delete(field);
          }
        }
        config.body = formData;
        this.skipCart = this.activeSubmitButton?.dataset.skipCart === 'true';

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
            //   if (this.skipCart) {
            //     window.location = "/checkout";
            //     return;
            // }

              const soldOutMessage = this.activeSubmitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.activeSubmitButton.setAttribute('aria-disabled', true);
              if (this.activeSubmitButtonText) this.activeSubmitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }else if (this.skipCart) {
                  window.location = "/checkout";
                  return;
              }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
          })
          .finally(() => {
            const hadError = !!this.error;
            const completionDetail = {
              source: 'product-form',
              success: !hadError,
            };
            if (this.activeSubmitButton) {
              this.activeSubmitButton.classList.remove('loading');
              this.activeSubmitButton.style.removeProperty('--spinner-color');
              this.activeSubmitButton.style.removeProperty('--loading-text-color');
              const spinnerElement =
                this.activeSubmitButton.querySelector('.loading__spinner') || this.querySelector('.loading__spinner');
              if (spinnerElement) {
                spinnerElement.style.removeProperty('--spinner-color');
              }
              if (this.activeSubmitButton.dataset.productFormRole) {
                completionDetail.submitterRole = this.activeSubmitButton.dataset.productFormRole;
              }
            }
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!hadError && this.activeSubmitButton) this.activeSubmitButton.removeAttribute('aria-disabled');
            if (activeSpinner) {
              activeSpinner.classList.add('hidden');
            }
            document.dispatchEvent(new CustomEvent('order-now-proxy:complete', { detail: completionDetail }));
            this.activeSubmitButton = null;
            this.activeSubmitButtonText = null;
            this.error = false;
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
